import { ReactNode } from 'react';
import {
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
} from 'remotion';

// Components
import { DotGrid } from '@/components/common/DotGrid';
import { CurtainRevealAnimation } from '@/components/common/CurtainRevealAnimation';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Utils
import { combineClasses } from '@/utils/className';

// Constants
import {
  BOARD_ANIM,
  CLAMP_RIGHT,
  SCENE_2_CONFIG,
  BOARD_ZOOM,
  RIBBON_ANIM_DELAY,
} from '../constants';
import { RibbonAnimation } from '../RibbonAnimation';

interface BoardProps {
  children?: ReactNode;
  showAnniversary?: boolean;
  sceneDuration?: number;
}

const BOARD_LAYOUT = {
  CONTAINER: { left: 310, top: 90, width: 1202, height: 900 },
  NOTCH: { width: 26, height: 284 },
  DOT_GRID: { left: 553, width: 482 },
} as const;

const CURTAIN_CONFIG = {
  COLUMNS: 12,
  COLUMN_DURATION: 20,
  STAGGER_DELAY: 2,
} as const;

// Curtain reveal completes when last column finishes
const CURTAIN_END_FRAME =
  BOARD_ANIM.DELAY +
  (CURTAIN_CONFIG.COLUMNS - 1) * CURTAIN_CONFIG.STAGGER_DELAY +
  CURTAIN_CONFIG.COLUMN_DURATION;

export const Board = ({
  children,
  showAnniversary = false,
  sceneDuration = SCENE_2_CONFIG.DURATION,
}: BoardProps) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Board entrance (scale + opacity)
  const progress = spring({
    frame: frame - BOARD_ANIM.DELAY,
    fps,
    config: BOARD_ANIM.SPRING_CONFIG,
  });

  const scale = interpolate(
    progress,
    [0, 1],
    [BOARD_ANIM.SCALE_START, 1],
    CLAMP_RIGHT,
  );

  const opacity = interpolate(progress, [0, 0.5, 1], [0, 1, 1], CLAMP_RIGHT);

  // Background zoom starts after curtain reveal, ends at sceneDuration
  const boardBackgroundScale = interpolate(
    frame,
    [CURTAIN_END_FRAME, sceneDuration],
    [BOARD_ZOOM.SCALE_START, BOARD_ZOOM.SCALE_END],
    CLAMP_RIGHT,
  );

  return (
    <>
      <div
        className="absolute"
        style={{
          ...BOARD_LAYOUT.CONTAINER,
          transform: `scale(${scale})`,
          opacity,
          transformOrigin: 'center',
        }}
      >
        {/* Background with zoom */}
        <div
          className="absolute inset-0"
          style={{
            transform: `scale(${boardBackgroundScale})`,
            transformOrigin: 'center',
            willChange: 'transform',
          }}
        >
          {/* Curtain Reveal */}
          <CurtainRevealAnimation
            columns={CURTAIN_CONFIG.COLUMNS}
            delay={BOARD_ANIM.DELAY}
            columnDuration={CURTAIN_CONFIG.COLUMN_DURATION}
            staggerDelay={CURTAIN_CONFIG.STAGGER_DELAY}
            showShadow={true}
            shadowIntensity={0.2}
          >
            <div className="absolute inset-0">
              {/* Main White Board Background */}
              <div className="absolute inset-0 bg-sub-gradient" />

              {/* Notch (Left Strip) */}
              <div
                className={combineClasses(
                  'absolute -left-px top-0',
                  employeeYearInReviewTheme.colors.bgSlateLight,
                )}
                style={BOARD_LAYOUT.NOTCH}
              />

              {/* Dark Overlay with Dot Grid (Right Side) */}
              <DotGrid
                className={combineClasses(
                  'opacity-70 absolute top-0 h-full',
                  employeeYearInReviewTheme.colors.bgSlateLight,
                )}
                style={BOARD_LAYOUT.DOT_GRID}
              />
            </div>
          </CurtainRevealAnimation>
        </div>

        {/* Content Container */}
        <div className="absolute inset-0">{children}</div>
      </div>

      {showAnniversary && (
        <Sequence className="inset-0" from={RIBBON_ANIM_DELAY}>
          <RibbonAnimation />
        </Sequence>
      )}
    </>
  );
};
